// ============================================================
// StatementPanel.tsx — B4: the case's SETTLEMENT / COMPLETION STATEMENT, at
// the foot of the Settlement section (backend settlementStatementController).
//   • the draft: settlement date, damages received date, interim / final,
//     retention + reason, where the net goes (+ detail), who signs — read
//     first (a summary + pencil), Save / Cancel only while editing;
//   • why it can't issue yet — each reason in plain words with a button that
//     does the fix (server-enforced: Issue refuses while any holds);
//   • maker / checker: "Mark as prepared" (fee earner) → "Approve" (an admin /
//     bill approver, not the preparer — ticking "I've reviewed these notes"
//     when there are ADVISORY notes, which never block) → "Issue statement";
//     once prepared the details lock — "Send back for changes" unlocks them;
//   • a child's / protected party's statement can issue as PROPOSED before the
//     court order (for the litigation friend + the approval hearing); the
//     final statement is the next version, once the order is recorded;
//   • versions: v1 Issued … "Superseded by v2"; a correction = a new version;
//   • an issued statement: preview / print, send (as bills), post / hand,
//     the client's answer (adults: AGREED / DISPUTED + amount), the court's
//     approval (children / protected parties), and the B4b hand-off —
//     "Ready for client account" with the net, the deductions and anything held.
//
// (Batch 2) Split into statement/*: DraftEditor (read-first details, lock +
// send back, the action box, maker / checker), CourtApproval (court order
// summary + step tracker), IssuedStatement (issued summary card, the
// client's answer, hand-off, versions). This file loads the case's
// statements, runs each write (busy, error, the fresh list — or a re-read on
// a `{ saved: true, reload: true }` reply), tells the rest of the screen
// (emitCaseChange) and re-reads when another part changes what it shows
// (useCaseChange — audit M15).
// (B4c batch 1b) E-signature (pilot): the send window's "Send for
// e-signature", and on the issued card the request's status with Resend /
// Cancel / View signed PDF / Evidence pack.
// (E-sign audit fixes A) Issuing / correcting cancels the old version's open
// signing links; if any couldn't be cancelled the reply says so
// (esignRevokeWarning) — shown as a warning after that action.
// (E-sign fixes B) LIVE for everyone viewing the case (hooks/useCaseLive):
// a 'case-changed' (topics settlement / esign) or a reconnect re-reads the
// statements — the status chip, "Signed online by…", the client's answer,
// the money split and the e-sign status update without a reload; while a
// request is still moving (sent / opened / code verified / sealing) a
// fallback poll every 20 s (visible tab only). A live change that altered
// what we show is announced to the rest of the screen (caseChange
// 'statement'). E-sign refusals (the server's reason — ESIGN_OFF …) show
// under the e-sign status, not as a bare 404; the pilot off → said there.
// (D12) A paper request → the Send window on Posted or handed over; "not me"
// → a new request (Send on e-signature); the redacted evidence pack. The
// bell's statement links (statement/statementFocus) scroll to the statement
// and, for the colleague-confirm alerts, open its Send window on e-signature.
// (Big-audit fix batch 1) A request fully signed on an earlier version (a
// correction no longer cancels it) shows on the current card as "Signed on
// an earlier version (version N)"; each e-sign action's busy label / refusal
// shows on the request it was for (a 409's plain message via ErrorNote).
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  checkStatementApi, checkStatementMismatchApi, approveStatementMismatchApi, correctStatementApi, getCaseStatementsApi, getStatementDocumentApi, issueStatementApi, prepareStatementApi,
  recordClientApprovalApi, recordLaterInstructionApi, approveLaterInstructionApi, refuseLaterInstructionApi, recordCourtOrderApi, recordCourtStageApi, saveStatementDraftApi, sendBackStatementApi,
  cancelStatementEsignApi, getEvidencePackApi, getSigningEvidenceApi, resendStatementEsignApi,
} from '../../api/casevoApi'
import { downloadBlob } from '../esign/esignFormat'
import type { BlockReason, CaseStatements, FixTarget, StatementSummary } from '../../types/settlementStatement.types'
import { B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, BIcon, CARD, NOTE, errorText } from '../billing/billingUi'
import { Banner, FigureCard } from './settlementUi'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import ModalFrame from '../shared/ModalFrame'
import DocumentPreviewFrame from '../shared/DocumentPreviewFrame'
import { buildSettlementStatementHtml, type StatementHtmlDoc } from '../../utils/settlementStatementHtml'
import { documentTheme } from '../../utils/documentTheme'
import { clientDocumentFonts } from '../../utils/documentFontFiles'
import { printCasePdf } from '../../utils/exportCasePdf'
import { emitCaseChange, useCaseChange, type CaseChangeKind } from '../../utils/caseChangeEvents'
import { useCaseLive } from '../../hooks/useCaseLive'
import { isEsignInProgress, isFullySigned } from './statement/EsignStatus'
import StatementSendModal from './StatementSendModal'
import { clearStatementFocus, onStatementFocus, peekStatementFocus } from './statement/statementFocus'
import DraftEditor from './statement/DraftEditor'
import { IssuedCard, VersionList } from './statement/IssuedStatement'
import { StatusChip, isReloadReply, stRef } from './statement/statementBits'

export { StatusChip } from './statement/statementBits'
export { ClientApprovalForm, ClientAnswer, HandOff, VersionList } from './statement/IssuedStatement'
export { SEND_BACK_ID } from './statement/DraftEditor'

// The browser's copy of the statement (utils/settlementStatementHtml — the
// same builder as the issued PDF; backend parity test).
export const statementPreviewHtml = (doc: StatementHtmlDoc): string => {
  const f = doc.letterhead.firm as StatementHtmlDoc['letterhead']['firm'] & { accentColour?: string | null; secondaryColour?: string | null; headingFont?: string | null; bodyFont?: string | null }
  return buildSettlementStatementHtml(doc, documentTheme(f.accentColour, f.secondaryColour), clientDocumentFonts(f.headingFont, f.bodyFont))
}

// What changes elsewhere on the screen alters the statement's blockers /
// figures ("Figures changed after approval") — re-read on each.
// (Follow-ups) …and 'statement': sent back from the Settlement page's
// "Figures locked" box (our own announcements are skipped).
const WATCH: CaseChangeKind[] = ['settlementInputs', 'cfa', 'litigationFriend', 'clientDetails', 'funding', 'bill', 'statement']
// (E-sign fixes B) The server's live topics that change what this panel shows.
export const LIVE_TOPICS = ['settlement', 'esign'] as const
export const LIVE_POLL_MS = 20_000

// figuresUnsaved: (Audit fix batch 1, M5) something on the Settlement page is
// unsaved (a figures card, the CFA, an interim) — "Mark as prepared" waits,
// with this as the visible reason.
interface Props { caseId: string; onFix: (target: FixTarget) => void; figuresUnsaved?: string | null }

const StatementPanel: React.FC<Props> = ({ caseId, onFix, figuresUnsaved }) => {
  const [data, setData] = useState<CaseStatements | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // The last refusal, and which action it came from (a save's refusal clears
  // as soon as a detail changes).
  const [actionError, setActionError] = useState<{ key: string; message: string } | null>(null)
  const [blockedOnIssue, setBlockedOnIssue] = useState<BlockReason[] | null>(null)
  // (Audit fix batch 1, H2) Issue asks first. (Audit B2, low b) A bill
  // difference is checked + OK'd beforehand (its own two steps), not sent with it.
  const [confirmIssue, setConfirmIssue] = useState(false)
  const [preview, setPreview] = useState<{ id: string; html: string; title: string } | null>(null)
  const [sending, setSending] = useState<StatementSummary | null>(null)
  // (D12) Which way the Send window opens (a paper request → post / hand).
  const [sendMode, setSendMode] = useState<'manual' | 'esign' | undefined>(undefined)
  const openSend = (s: StatementSummary, mode?: 'manual' | 'esign'): void => { setSendMode(mode); setSending(s) }
  const [starting, setStarting] = useState(false)
  // (E-sign audit fixes A) The last action’s "the old signing link wasn’t cancelled" warning.
  const [esignWarning, setEsignWarning] = useState<string | null>(null)

  // (Audit fix batch 1, M6) Only the newest read's answer is used, and a
  // write's own reply outranks every read that started before it came back —
  // a slow event-driven re-read can't show "not prepared" after Prepare.
  const seq = useRef(0)
  const load = useCallback(async () => {
    const n = ++seq.current
    try { const r = await getCaseStatementsApi(caseId); if (n !== seq.current) return; setData(r); setLoadError(null) } catch (e) { if (n === seq.current) setLoadError(errorText(e, 'Couldn’t load the settlement statements.')) }
  }, [caseId])
  const applyReply = useCallback((r: CaseStatements): void => { seq.current++; setData(r) }, [])
  useEffect(() => { void load() }, [load])
  const ownEmit = useRef(false)
  const tell = useCallback((kind: CaseChangeKind) => {
    ownEmit.current = true
    try { emitCaseChange(caseId, kind) } finally { ownEmit.current = false }
  }, [caseId])
  useCaseChange(caseId, WATCH, (_k, info) => {
    if (ownEmit.current) return
    // (D12) A remote settlement / e-sign change is already re-read by useCaseLive below.
    if (info.remote && !info.resync && info.topics?.length && info.topics.every((t) => (LIVE_TOPICS as readonly string[]).includes(t))) return
    void load()
  })
  // (E-sign fixes B) Live: another user / the signer / the sealing job changed
  // something. A quiet re-read (a failed one keeps what's shown); when it
  // changed what we show, the rest of the screen is told.
  const dataRef = useRef<CaseStatements | null>(null)
  dataRef.current = data
  const liveLoad = useCallback(async () => {
    const n = ++seq.current
    try {
      const r = await getCaseStatementsApi(caseId)
      if (n !== seq.current) return
      const changed = !!dataRef.current && JSON.stringify(dataRef.current) !== JSON.stringify(r)
      setData(r); setLoadError(null)
      if (changed) tell('statement')
    } catch { /* keep what's shown; the next signal / poll tries again */ }
  }, [caseId, tell])
  const inProgress = Object.values(data?.esign ?? {}).some((e) => isEsignInProgress(e))
  useCaseLive(caseId, LIVE_TOPICS, () => { void liveLoad() }, { pollMs: LIVE_POLL_MS, pollWhile: inProgress })
  // (D12) The bell asked for a statement: scroll to it (the current one, else
  // the panel) and, for the colleague-confirm alerts, open Send on e-signature.
  const [focusNonce, setFocusNonce] = useState(0)
  useEffect(() => onStatementFocus(() => setFocusNonce((n) => n + 1)), [])
  useEffect(() => {
    const f = peekStatementFocus(caseId)
    if (!f || !data) return
    clearStatementFocus()
    const s = data.statements.find((x) => x.id === f.statementId) ?? null
    const el = (s && document.querySelector<HTMLElement>(`[data-statement-id="${s.id}"]`)) || document.getElementById('settlement-statement')
    el?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
    el?.focus?.({ preventScroll: true })
    if (s && f.openEsign && data.esignPilot && s.status !== 'DRAFT') { setSendMode('esign'); setSending(s) }
  }, [caseId, data, focusNonce])

  // One wrapper for every action: busy, error, the fresh list back (or a
  // re-read), then the rest of the screen is told.
  const run = async (key: string, fn: () => Promise<CaseStatements>, kind: CaseChangeKind = 'statement'): Promise<boolean> => {
    setBusy(key); setActionError(null); setBlockedOnIssue(null); setEsignWarning(null)
    try {
      const r = await fn()
      if (r.esignRevokeWarning) setEsignWarning(r.esignRevokeWarning)
      if (isReloadReply(r)) await load(); else applyReply(r)
      tell(kind)
      return true
    } catch (e) {
      const res = (e as { response?: { status?: number; data?: { blockers?: BlockReason[] } } }).response
      if (res?.data?.blockers?.length) setBlockedOnIssue(res.data.blockers)
      setActionError({ key, message: errorText(e, 'That didn’t work. Please try again.') })
      // Changed meanwhile (another user, the figures moved, or it's locked): show it as it is now.
      if (res?.status === 409) void load()
      return false
    } finally { setBusy(null) }
  }
  // (B4c 1b) E-signature actions — each reloads the statements after.
  const esignDo = async (key: string, fn: () => Promise<void>, fallback: string): Promise<boolean> => {
    setBusy(key); setActionError(null)
    try { await fn(); await load(); tell('statement'); return true } catch (e) {
      setActionError({ key, message: errorText(e, fallback) }); void load(); return false
    } finally { setBusy(null) }
  }
  // (Big-audit fix batch 1) Which statement's request the last e-sign action
  // was for — its busy label and refusal show on THAT request's status (the
  // current version's, or one signed on an earlier version).
  const [esignTarget, setEsignTarget] = useState<string | null>(null)
  const esignActionsFor = (s: StatementSummary) => {
    const e = data?.esign?.[s.id]
    const on = <A extends unknown[], R>(fn: (...a: A) => R) => (...a: A): R => { setEsignTarget(s.id); return fn(...a) }
    return {
      // A 409 (e.g. every signer has now signed) → the server's plain message under the status, then a re-read.
      resend: on(() => { void esignDo('esign:resend', async () => { await resendStatementEsignApi(caseId, s.id) }, 'The link wasn’t resent.') }),
      cancel: on((reason: string) => esignDo('esign:cancel', () => cancelStatementEsignApi(caseId, s.id, reason), 'The request wasn’t cancelled.')),
      viewSigned: on(() => {
        if (!e?.signedPdfId) return
        setBusy('esign:pdf')
        getSigningEvidenceApi(e.id, e.signedPdfId)
          .then((b) => { const url = URL.createObjectURL(new Blob([b], { type: 'application/pdf' })); window.open(url, '_blank', 'noopener'); setTimeout(() => URL.revokeObjectURL(url), 60_000) })
          .catch((err) => setActionError({ key: 'esign:pdf', message: errorText(err, 'Couldn’t open the signed PDF.') }))
          .finally(() => setBusy(null))
      }),
      evidencePack: on(() => pack(false)),
      // (D12) IP addresses, devices and reasons removed — for the other side.
      redactedPack: on(() => pack(true)),
      sendPaper: () => openSend(s, 'manual'),
      sendNew: () => openSend(s, 'esign'),
    }
    function pack(redacted: boolean): void {
      if (!e) return
      const key = redacted ? 'esign:pack-redacted' : 'esign:pack'
      setBusy(key)
      getEvidencePackApi(e.id, { redacted })
        .then((b) => downloadBlob(b, `Evidence pack ${e.reference}${redacted ? ' (redacted)' : ''}.zip`))
        .catch((err) => setActionError({ key, message: errorText(err, 'Couldn’t make the evidence pack.') }))
        .finally(() => setBusy(null))
    }
  }
  const clearSaveError = (): void => { setActionError((a) => (a && a.key === 'save' ? null : a)) }
  const openPreview = async (s: StatementSummary): Promise<void> => {
    setBusy(`preview:${s.id}`); setActionError(null)
    try {
      const r = await getStatementDocumentApi(caseId, s.id)
      setPreview({ id: s.id, html: statementPreviewHtml(r.doc), title: `Statement ${stRef(s)}` })
    } catch (e) { setActionError({ key: 'preview', message: errorText(e, 'Couldn’t load the statement.') }) } finally { setBusy(null) }
  }

  if (loadError) return <Banner tone="error" action={<button type="button" className={B_SECONDARY_SM} onClick={() => { void load() }}>Retry</button>}>{loadError}</Banner>
  if (!data) return <div className={`${CARD} p-5 text-[13px] text-casevo-text-soft`} aria-busy="true">Loading the settlement statement…</div>
  const draft = data.draft
  const issued = data.statements.filter((s) => s.status !== 'DRAFT')
  const current = issued.find((s) => s.status === 'ISSUED' || s.status === 'APPROVED') ?? null
  // (Big-audit fix batch 1) A request every signer signed on an EARLIER
  // version stays signed after a correction — shown on the current card as
  // "Signed on an earlier version (version N)", packs still available.
  // (Big-audit fix batch 2) …every one of them (folded on the card once the current version is signed).
  const earlier = current ? issued
    .filter((s) => s.version < current.version && isFullySigned(data.esign?.[s.id]))
    .sort((a, b) => b.version - a.version) : []
  const esignBusyFor = (id: string): string | null => (busy?.startsWith('esign:') && esignTarget && esignTarget !== id ? 'esign:other' : busy)
  const esignErrorFor = (id: string): string | null => (actionError?.key.startsWith('esign:') && (!esignTarget || esignTarget === id) ? actionError.message : null)

  return (
    <FigureCard icon="list" title="Settlement statement" label="Settlement statement"
      sub="The statement the client receives: damages, each deduction and why, and where the net sum goes."
      right={draft ? <StatusChip status="DRAFT" /> : current ? <StatusChip status={current.status} /> : null}>
      <div className="flex flex-col gap-4" id="settlement-statement">
        {/* (M7) A refused send-back is said inside its dialog, not here behind it. */}
        {actionError && !blockedOnIssue && actionError.key !== 'sendBack' && !actionError.key.startsWith('esign:') && <Banner tone="error">{actionError.message}</Banner>}
        {esignWarning && <div data-esign-revoke-warning=""><Banner tone="warn" title="The old version’s signing link is still open">{esignWarning}</Banner></div>}
        {!data.wording.ok && <Banner tone="warn" action={<button type="button" className={B_SECONDARY_SM} onClick={() => onFix('settlementWording')}>Open Settlement wording</button>}>{data.wording.reason}</Banner>}

        {draft ? (
          <DraftEditor key={draft.id} data={data} draft={draft} busy={busy} onFix={onFix} onEdit={clearSaveError} blockedOnIssue={blockedOnIssue}
            // (Audit fix batch 1, H3) The body carries the version the editor
            // OPENED from (baseUpdatedAt) and only the fields that changed.
            save={(body) => run('save', () => saveStatementDraftApi(caseId, body))}
            prepare={() => run('prepare', () => prepareStatementApi(caseId, draft.id))}
            check={(review) => run('check', () => checkStatementApi(caseId, draft.id, draft.approvalFingerprint, review))}
            issue={() => setConfirmIssue(true)}
            checkMismatch={(reason) => run('mismatchCheck', () => checkStatementMismatchApi(caseId, draft.id, { checked: true, reason, key: draft.billMismatch?.key ?? '' }))}
            approveMismatch={() => run('mismatchApprove', () => approveStatementMismatchApi(caseId, draft.id, draft.billMismatch?.key ?? ''))}
            figuresUnsaved={figuresUnsaved ?? null}
            preview={() => { void openPreview(draft) }}
            recordOrder={(body) => run('order', () => recordCourtOrderApi(caseId, draft.id, body), 'courtOrder')}
            recordStage={(body) => run('stage', () => recordCourtStageApi(caseId, draft.id, body), 'courtOrder')}
            sendBack={(reason) => run('sendBack', () => sendBackStatementApi(caseId, draft.id, reason))}
            sendBackError={actionError?.key === 'sendBack' ? actionError.message : null} />
        ) : starting ? (
          <DraftEditor data={data} draft={null} busy={busy} onFix={onFix} onEdit={clearSaveError}
            save={async (body) => { const ok = await run('save', () => saveStatementDraftApi(caseId, body)); if (ok) setStarting(false); return ok }}
            onCancel={() => { setStarting(false); clearSaveError() }} />
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <p className={`${NOTE} m-0 flex-1`}>{issued.length ? 'Start another statement (for example the final one after an interim), or correct the current one below.' : 'No statement yet. Start one when the damages are agreed.'}</p>
            <button type="button" className={B_PRIMARY} onClick={() => setStarting(true)}><BIcon name="plus" size={13} />Start a statement</button>
          </div>
        )}

        {current && (
          <IssuedCard s={current} data={data} busy={busy}
            preview={() => { void openPreview(current) }}
            send={() => openSend(current)}
            correct={() => run('correct', () => correctStatementApi(caseId, current.id))}
            approve={(body) => run('approval', () => recordClientApprovalApi(caseId, current.id, body))}
            caseId={caseId}
            later={{
              record: (body) => run('later', () => recordLaterInstructionApi(caseId, current.id, body)),
              approve: (insId) => run('laterApprove', () => approveLaterInstructionApi(caseId, current.id, insId)),
              refuse: (insId, reason) => run('laterRefuse', () => refuseLaterInstructionApi(caseId, current.id, insId, reason)),
            }}
            recordStage={(body) => run('stage', () => recordCourtStageApi(caseId, current.id, body), 'courtOrder')}
            esignActions={esignActionsFor(current)} esignPilotOff={!data.esignPilot}
            esignError={esignErrorFor(current.id)} esignBusy={esignBusyFor(current.id)}
            earlierSigned={earlier.flatMap((x) => { const e = data.esign?.[x.id]; return e ? [{ version: x.version, e, actions: esignActionsFor(x), busy: esignBusyFor(x.id), error: esignErrorFor(x.id) }] : [] })}
            hasDraft={!!draft} />
        )}

        {issued.length > 0 && <VersionList statements={issued} busy={busy} representativeType={data.representativeType} onPreview={(s) => { void openPreview(s) }} />}
      </div>

      {confirmIssue && draft && (
        <ConfirmDialogue isOpen title={`Issue statement ${draft.number ?? ''} v${draft.version}?`.replace('  ', ' ')}
          message="This gives the statement its number, freezes the figures, the wording and the signatory, and saves the PDF to the case. An issued statement can’t be changed — a correction is a new version."
          confirmLabel="Issue statement"
          onCancel={() => setConfirmIssue(false)}
          onConfirm={() => { setConfirmIssue(false); void run('issue', () => issueStatementApi(caseId, draft.id)) }} />
      )}
      {preview && (
        <ModalFrame size="xxl" onClose={() => setPreview(null)} eyebrow="Settlement statement" title={preview.title}
          footerRight={<><button type="button" className={B_SECONDARY} onClick={() => setPreview(null)}>Close</button>
            <button type="button" className={B_PRIMARY} onClick={() => printCasePdf(preview.html)}><BIcon name="print" size={13} />Print / save PDF</button></>}>
          <DocumentPreviewFrame title={preview.title} html={preview.html} />
        </ModalFrame>
      )}
      {/* (Audit fix batch 1, Low 13) ONE announcement per send (the window
          itself announces nothing): the statement, and the bill when one went with it. */}
      {sending && <StatementSendModal caseId={caseId} statement={sending} esignPilot={!!data.esignPilot} onFix={onFix} initialMode={sendMode} onClose={() => setSending(null)} onDone={(d, withBill) => { setSending(null); if (d && !isReloadReply(d)) applyReply(d); else void load(); tell('statement'); if (withBill) tell('bill') }} />}
    </FigureCard>
  )
}

export default StatementPanel
